import { useState } from "react";
import { FaUtensils } from "react-icons/fa";
import { FiGrid, FiInfo, FiSearch, FiShoppingBag } from "react-icons/fi";
import { useLocation } from "react-router-dom";
import { categoryIconsMap } from "../../../config/categoryIcons";
import { formatPersianPrice, getCategoryKey, parseTableNumber } from "../../../services/customerMenuModel";
import MenuIntro from "../components/MenuIntro";
import MenuSplash from "../components/MenuSplash";

const persianNumber = new Intl.NumberFormat("fa-IR");
const GREEN = "#153A2E";
const GOLD = "#C79A4B";
const CREAM = "#F8F5EC";

const SignatureItemCard = ({ item, quantity = 0, onAdd, onDecrease, onClick }) => (
  <article className="flex gap-3 rounded-2xl bg-white p-3 shadow-sm">
    <button type="button" onClick={() => onClick(item)} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl" style={{ backgroundColor: "#EFE9D8" }}>
      {item.image ? <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" /> : <span className="flex h-full w-full items-center justify-center text-2xl" style={{ color: GOLD }}><FaUtensils /></span>}
    </button>
    <button type="button" onClick={() => onClick(item)} className="min-w-0 flex-1 text-right focus:outline-none">
      <h3 className="font-bold" style={{ color: GREEN }}>{item.name}</h3>
      {item.description && <p className="mt-1 line-clamp-1 text-xs text-gray-500">{item.description}</p>}
      <p className="mt-2 text-sm font-black" style={{ color: GOLD }}>{formatPersianPrice(item.price)}</p>
    </button>
    <div className="flex shrink-0 items-end">
      {quantity > 0 ? (
        <div className="flex items-center gap-1.5 rounded-full px-1 py-1" style={{ backgroundColor: "#EFE9D8" }}>
          <button type="button" onClick={() => onDecrease(item)} aria-label={`کم کردن ${item.name}`} className="flex h-7 w-7 items-center justify-center rounded-full" style={{ color: GREEN }}>−</button>
          <span className="w-4 text-center text-xs font-bold" style={{ color: GREEN }}>{persianNumber.format(quantity)}</span>
          <button type="button" onClick={() => onAdd(item)} aria-label={`اضافه کردن ${item.name}`} className="flex h-7 w-7 items-center justify-center rounded-full text-white" style={{ backgroundColor: GREEN }}>+</button>
        </div>
      ) : (
        <button type="button" onClick={() => onAdd(item)} aria-label={`افزودن ${item.name} به سبد`} className="flex h-9 w-9 items-center justify-center rounded-full text-white shadow" style={{ backgroundColor: GREEN }}>+</button>
      )}
    </div>
  </article>
);

const MizbanSignatureMenuTheme = (props) => {
  const {
    menuData, restaurant, settings, profileImage, coverImage, categories, visibleItems,
    activeCategoryKey, setActiveCategoryKey, searchTerm, setSearchTerm, quantities, cartSummary,
    addItem, decreaseItem, openItem, openCart, showBamizbanBadge,
  } = props;
  const [infoOpen, setInfoOpen] = useState(false);
  const location = useLocation();
  const tableNumber = parseTableNumber(location.search);

  return (
    <div className="min-h-screen pb-28" dir="rtl" style={{ backgroundColor: CREAM }}>
      <MenuSplash
        restaurantName={restaurant.name || menuData.title}
        profileImage={profileImage}
        backgroundColor={GREEN}
        accentColor={GOLD}
        tagline="میزبانی با افتخار"
      />
      <section className="relative h-56 overflow-hidden sm:h-64" style={{ backgroundColor: GREEN }}>
        <img src={coverImage} alt="" className="h-full w-full object-cover opacity-55" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, rgba(0,0,0,0.3), ${GREEN}CC 60%, ${CREAM})` }} />
        <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
          <button type="button" onClick={() => setInfoOpen(true)} aria-label="اطلاعات رستوران" className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow" style={{ color: GREEN }}><FiInfo /></button>
          {tableNumber && <span className="rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold shadow" style={{ color: GREEN }}>میز {persianNumber.format(tableNumber)}</span>}
        </div>
        <div className="absolute inset-x-0 bottom-4 flex flex-col items-center text-center">
          <img src={profileImage} alt="" className="h-16 w-16 rounded-full border-2 object-cover" style={{ borderColor: GOLD }} />
          <h1 className="mt-2 text-xl font-black text-white">{restaurant.name || menuData.title}</h1>
          {settings.welcomeText && <p className="mt-1 max-w-xs text-xs leading-6 text-white/85">{settings.welcomeText}</p>}
        </div>
      </section>

      <main className="mx-auto max-w-3xl px-4 pt-4">
        <div className="sticky top-0 z-30 mx-auto -mx-4 px-4 pb-3 pt-1 backdrop-blur" style={{ backgroundColor: `${CREAM}F2` }}>
          <label className="relative block">
            <span className="sr-only">جست‌وجوی منو</span>
            <FiSearch className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} placeholder="جست‌وجوی غذا و نوشیدنی" className="min-h-12 w-full rounded-full border-0 bg-white py-3 pl-4 pr-11 text-sm shadow-sm outline-none focus:ring-2" style={{ "--tw-ring-color": GOLD }} />
          </label>

          <div className="mt-4 flex gap-4 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" aria-label="دسته‌بندی‌های منو">
            <button type="button" aria-pressed={activeCategoryKey === "all"} onClick={() => setActiveCategoryKey("all")} className="flex shrink-0 flex-col items-center gap-1.5">
              <span className="flex h-14 w-14 items-center justify-center rounded-full text-xl shadow-sm" style={activeCategoryKey === "all" ? { backgroundColor: GREEN, color: "#fff" } : { backgroundColor: "#fff", color: GREEN }}><FiGrid /></span>
              <span className="text-[11px] font-bold" style={{ color: GREEN }}>همه</span>
            </button>
            {categories.map((category) => {
              const key = getCategoryKey(category);
              const Icon = categoryIconsMap[category.icon] || FaUtensils;
              const active = activeCategoryKey === key;
              return (
                <button key={key} type="button" aria-pressed={active} onClick={() => setActiveCategoryKey(key)} className="flex shrink-0 flex-col items-center gap-1.5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-full text-xl shadow-sm" style={active ? { backgroundColor: GREEN, color: "#fff" } : { backgroundColor: "#fff", color: GREEN }}><Icon /></span>
                  <span className="max-w-16 truncate text-[11px] font-bold" style={{ color: GREEN }}>{category.name}</span>
                </button>
              );
            })}
          </div>
        </div>

        {visibleItems.length ? (
          <div className="mx-auto mt-2 grid gap-3 pb-6 md:grid-cols-2">
            {visibleItems.map((item) => (
              <SignatureItemCard key={item._id} item={item} quantity={quantities[item._id]} onAdd={addItem} onDecrease={decreaseItem} onClick={openItem} />
            ))}
          </div>
        ) : (
          <div className="mx-auto mt-4 max-w-2xl rounded-2xl border border-dashed px-4 py-14 text-center" style={{ borderColor: "#DED2B0", backgroundColor: "#ffffffb3" }}>
            <FaUtensils className="mx-auto text-3xl" style={{ color: "#CBB98A" }} aria-hidden="true" />
            <p className="mt-3 font-bold" style={{ color: GREEN }}>آیتمی پیدا نشد</p>
            <p className="mt-1 text-sm text-gray-500">عبارت جست‌وجو یا دسته‌بندی را تغییر دهید.</p>
            {searchTerm && <button type="button" onClick={() => { setSearchTerm(""); setActiveCategoryKey("all"); }} className="mt-4 text-sm font-bold" style={{ color: GOLD }}>پاک کردن جست‌وجو</button>}
          </div>
        )}

        {showBamizbanBadge && <p className="pb-2 pt-4 text-center text-[11px] text-gray-400">ساخته شده با <span className="font-bold text-gray-500">بامیزبان</span></p>}
      </main>

      {cartSummary.itemCount > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 p-3" dir="rtl">
          <button type="button" onClick={openCart} className="mx-auto flex min-h-12 w-full max-w-xl items-center justify-between rounded-full px-5 font-bold text-white shadow-lg" style={{ backgroundColor: GREEN }}>
            <span className="flex items-center gap-2"><FiShoppingBag /> مشاهده سبد <span className="rounded-full px-2 py-0.5 text-xs" style={{ backgroundColor: GOLD, color: GREEN }}>{persianNumber.format(cartSummary.itemCount)} آیتم</span></span>
            <span>{formatPersianPrice(cartSummary.total)}</span>
          </button>
        </div>
      )}

      <MenuIntro open={infoOpen} restaurantData={restaurant} profileImage={profileImage} onClose={() => setInfoOpen(false)} />
    </div>
  );
};

export default MizbanSignatureMenuTheme;
